import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTree, useTreeEditMutation } from "../api/queries";
import type { EnvConfig, Me, MergePolicy, PromotionConfig, TreeEditChange, TreeEditSet } from "../api/types";
import Spinner from "../components/Spinner";
import UserMenu from "../components/UserMenu";
import { showToast } from "../util/toast";

interface FormState {
    dependsOn: string; // "" => null (canary head)
    soakDays: string;
    windows: string[];
    mergePolicy: MergePolicy;
    delaySeconds: string;
}

export default function TreeEditorPage({ me }: { me: Me }) {
    const navigate = useNavigate();
    const location = useLocation();
    const treeQ = useTree();
    const submitMut = useTreeEditMutation();
    const config = treeQ.data?.config ?? null;

    const [selected, setSelected] = useState("");
    const [form, setForm] = useState<FormState | null>(null);
    const [staged, setStaged] = useState<Record<string, TreeEditSet>>({});
    const [summary, setSummary] = useState("");

    const selectEnv = (key: string) => {
        setSelected(key);
        const env = config?.envs[key];
        setForm(env ? formFromConfig(env, staged[key]) : null);
    };

    const stage = () => {
        if (!config || !selected || !form) return;
        const env = config.envs[selected];
        if (!env) return;
        const result = buildSet(env, form);
        if ("error" in result) {
            showToast({ kind: "error", message: result.error });
            return;
        }
        if (Object.keys(result.set).length === 0) {
            showToast({ kind: "info", message: `No changes for ${selected}` });
            return;
        }
        const next = { ...staged, [selected]: result.set };
        const cycle = findCycle(config, next);
        if (cycle) {
            showToast({ kind: "error", message: `Dependency cycle: ${cycle.join(" → ")}` });
            return;
        }
        setStaged(next);
    };

    const unstage = (key: string) => {
        setStaged((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const changes: TreeEditChange[] = Object.entries(staged).map(([envKey, set]) => ({ envKey, set }));
    const canSubmit = me.role === "promoter" && changes.length > 0 && summary.trim() !== "" && !submitMut.isPending;

    const submit = () => {
        if (!canSubmit) return;
        submitMut.mutate(
            { changes, summary: summary.trim() },
            {
                onSuccess: (edit) => {
                    showToast({
                        kind: "success",
                        message: "Tree edit submitted",
                        link: edit.mr ? { url: edit.mr.webUrl, label: `MR !${edit.mr.iid}` } : undefined,
                    });
                    navigate({ pathname: "/", search: "?fleet=argots" });
                },
                onError: (err) =>
                    showToast({ kind: "error", message: `Tree edit failed: ${err instanceof Error ? err.message : String(err)}` }),
            },
        );
    };

    return (
        <div className="app">
            <header className="header">
                <h1>Promotion Tree Editor</h1>
                <Link className="btn btn-small" to={{ pathname: "/", search: location.search || "?fleet=argots" }}>
                    ← Back to dashboard
                </Link>
                <div className="header-right">
                    <UserMenu me={me} />
                </div>
            </header>
            <main>
                {treeQ.isPending ? <Spinner label="Loading promotion config…" /> : null}
                {treeQ.isError ? <div className="banner red">Failed to load tree: {treeQ.error.message}</div> : null}
                {treeQ.data && !config ? <div className="empty">No promotion config available.</div> : null}
                {me.role !== "promoter" ? <div className="banner yellow">Promoter role required to submit tree edits.</div> : null}

                {config ? (
                    <div className="editor-layout">
                        <section className="editor-form">
                            <label className="field">
                                Environment
                                <select value={selected} onChange={(e) => selectEnv(e.target.value)}>
                                    <option value="">— select env —</option>
                                    {Object.keys(config.envs)
                                        .sort()
                                        .map((k) => (
                                            <option key={k} value={k}>
                                                {k}
                                                {staged[k] ? " (staged)" : ""}
                                            </option>
                                        ))}
                                </select>
                            </label>

                            {form && selected ? (
                                <>
                                    <label className="field">
                                        dependsOn
                                        <select value={form.dependsOn} onChange={(e) => setForm({ ...form, dependsOn: e.target.value })}>
                                            <option value="">null (canary head)</option>
                                            {Object.keys(config.envs)
                                                .filter((k) => k !== selected)
                                                .sort()
                                                .map((k) => (
                                                    <option key={k} value={k}>
                                                        {k}
                                                    </option>
                                                ))}
                                        </select>
                                    </label>
                                    <label className="field">
                                        soakDays
                                        <input
                                            type="number"
                                            min={0}
                                            value={form.soakDays}
                                            onChange={(e) => setForm({ ...form, soakDays: e.target.value })}
                                        />
                                    </label>
                                    <fieldset className="field">
                                        <legend>windows</legend>
                                        {Object.entries(config.slots).map(([slot, sc]) => (
                                            <label key={slot} className="checkbox" title={`${sc.cron} (${sc.tz})`}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.windows.includes(slot)}
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            windows: e.target.checked
                                                                ? [...form.windows, slot]
                                                                : form.windows.filter((w) => w !== slot),
                                                        })
                                                    }
                                                />
                                                {slot}
                                                <span className="muted"> — {sc.desc ?? sc.cron}</span>
                                            </label>
                                        ))}
                                        {Object.keys(config.slots).length === 0 ? <p className="muted">no slots defined</p> : null}
                                    </fieldset>
                                    <label className="field">
                                        mergePolicy
                                        <select
                                            value={form.mergePolicy}
                                            onChange={(e) => setForm({ ...form, mergePolicy: e.target.value as MergePolicy })}
                                        >
                                            <option value="auto">auto</option>
                                            <option value="delayed">delayed</option>
                                            <option value="manual">manual</option>
                                        </select>
                                    </label>
                                    <label className="field">
                                        delaySeconds
                                        <input
                                            type="number"
                                            min={0}
                                            value={form.delaySeconds}
                                            onChange={(e) => setForm({ ...form, delaySeconds: e.target.value })}
                                        />
                                    </label>
                                    <button className="btn" onClick={stage}>
                                        Stage change
                                    </button>
                                </>
                            ) : (
                                <p className="muted">Select an environment to edit.</p>
                            )}
                        </section>

                        <section className="editor-staged">
                            <h3>Staged changes</h3>
                            {changes.length === 0 ? <p className="muted">none yet</p> : null}
                            {changes.map((c) => (
                                <div key={c.envKey} className="staged-row">
                                    <strong>{c.envKey}</strong>
                                    <span>{describeSet(c.set)}</span>
                                    <button className="btn btn-small" onClick={() => unstage(c.envKey)}>
                                        Remove
                                    </button>
                                </div>
                            ))}
                            <label className="field">
                                Summary
                                <input
                                    type="text"
                                    placeholder="Why this change?"
                                    value={summary}
                                    onChange={(e) => setSummary(e.target.value)}
                                />
                            </label>
                            <button className="btn btn-primary" disabled={!canSubmit} onClick={submit}>
                                {submitMut.isPending ? "Submitting…" : "Submit tree edit"}
                            </button>
                        </section>
                    </div>
                ) : null}
            </main>
        </div>
    );
}

function formFromConfig(env: EnvConfig, staged: TreeEditSet | undefined): FormState {
    const dependsOn = staged?.dependsOn !== undefined ? staged.dependsOn : env.dependsOn;
    const soakDays = staged?.soakDays !== undefined ? staged.soakDays : env.soakDays;
    const windows = staged?.windows != null ? staged.windows : (env.windows ?? []);
    const mergePolicy = staged?.mergePolicy !== undefined ? staged.mergePolicy : env.mergePolicy;
    const delaySeconds = staged?.delaySeconds !== undefined ? staged.delaySeconds : env.delaySeconds;
    return {
        dependsOn: dependsOn ?? "",
        soakDays: soakDays !== undefined ? String(soakDays) : "",
        windows: [...windows],
        mergePolicy,
        delaySeconds: delaySeconds !== undefined ? String(delaySeconds) : "",
    };
}

// Only fields that actually differ from the current config go into the change set.
function buildSet(env: EnvConfig, form: FormState): { set: TreeEditSet } | { error: string } {
    const set: TreeEditSet = {};
    const dependsOn = form.dependsOn === "" ? null : form.dependsOn;
    if (dependsOn !== env.dependsOn) set.dependsOn = dependsOn;
    if (form.soakDays.trim() !== "") {
        const n = Number(form.soakDays);
        if (!Number.isInteger(n) || n < 0) return { error: "soakDays must be a non-negative integer" };
        if (n !== env.soakDays) set.soakDays = n;
    }
    const next = [...form.windows].sort().join("|");
    const curr = [...(env.windows ?? [])].sort().join("|");
    if (next !== curr) set.windows = form.windows;
    if (form.mergePolicy !== env.mergePolicy) set.mergePolicy = form.mergePolicy;
    if (form.delaySeconds.trim() !== "") {
        const n = Number(form.delaySeconds);
        if (!Number.isInteger(n) || n < 0) return { error: "delaySeconds must be a non-negative integer" };
        if (n !== env.delaySeconds) set.delaySeconds = n;
    }
    return { set };
}

function describeSet(set: TreeEditSet): string {
    const parts: string[] = [];
    if (set.dependsOn !== undefined) parts.push(`dependsOn → ${set.dependsOn ?? "null (canary head)"}`);
    if (set.soakDays !== undefined) parts.push(`soakDays → ${set.soakDays}`);
    if (set.windows !== undefined) parts.push(`windows → ${set.windows?.join(", ") || "none"}`);
    if (set.mergePolicy !== undefined) parts.push(`mergePolicy → ${set.mergePolicy}`);
    if (set.delaySeconds !== undefined) parts.push(`delaySeconds → ${set.delaySeconds}`);
    return parts.join("; ");
}

// Walk each env up its (staged) dependsOn chain; a revisit means a cycle.
function findCycle(config: PromotionConfig, staged: Record<string, TreeEditSet>): string[] | null {
    const parentOf = (key: string): string | null => {
        const s = staged[key];
        if (s && s.dependsOn !== undefined) return s.dependsOn;
        return config.envs[key]?.dependsOn ?? null;
    };
    for (const start of Object.keys(config.envs)) {
        const seen = new Set<string>([start]);
        const path = [start];
        let cur = parentOf(start);
        while (cur !== null) {
            if (seen.has(cur)) return [...path, cur];
            seen.add(cur);
            path.push(cur);
            if (!(cur in config.envs)) break;
            cur = parentOf(cur);
        }
    }
    return null;
}
